import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTable, useFilters, usePagination } from 'react-table';
import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { withRouter } from 'react-router-dom';
import * as invoiceDateActions from '../../actions/invoiceDateActions';
import { notify } from '../../util/notifications';
import AppPagination, { ALL_ROWS } from '../../components/common/AppPagination';

const PRIORITY_COLORS = {
    1: '#CB0000',
    2: '#800080',
    3: '#F36E07',
    4: '#E0B400',
    5: '#008000',
    6: '#3084FA',
    7: '#5B3A2E',
    8: '#A7AFB9',
    9: '#000000',
};
const priorityColor = (priority) => PRIORITY_COLORS[priority] || '#757575';

const ColumnFilter = ({ column: { filterValue, setFilter, filterLabel } }) => (
    <TextField
        value={filterValue || ''}
        onChange={(e) => setFilter(e.target.value || undefined)}
        placeholder={filterLabel || 'Filter'}
        fullWidth
        inputProps={{ 'aria-label': filterLabel || 'Filter' }}
        sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: 13 } }}
    />
);

// Counts up from the elapsed "MM:SS" the API sends. Kept outside the dashboard so the
// 60-second refresh doesn't reset every timer.
const ElapsedTimeCell = ({ startTime }) => {
    const [minutes, seconds] = String(startTime || '0:0').split(':').map(Number);
    const [elapsedSeconds, setElapsedSeconds] = useState((minutes || 0) * 60 + (seconds || 0));

    useEffect(() => {
        const intervalId = setInterval(() => setElapsedSeconds((prev) => prev + 1), 1000);
        return () => clearInterval(intervalId);
    }, []);

    const totalMinutes = Math.floor(elapsedSeconds / 60);
    let label;
    if (totalMinutes >= 24 * 60) {
        label = `${Math.floor(totalMinutes / 1440)}d ${Math.floor((totalMinutes % 1440) / 60)}h`;
    } else if (totalMinutes >= 60) {
        label = `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`;
    } else {
        label = `${totalMinutes.toString().padStart(2, '0')}:${(elapsedSeconds % 60).toString().padStart(2, '0')}`;
    }
    return (
        <Typography component="span" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500, whiteSpace: 'nowrap' }}>
            {label}
        </Typography>
    );
};

const TwoLine = ({ top, bottom }) => (
    <Box sx={{ lineHeight: 1.35 }}>
        <Box>{top || '—'}</Box>
        <Box sx={{ color: 'text.secondary', fontSize: 13 }}>{bottom || '—'}</Box>
    </Box>
);

const Place = ({ label, value }) => (
    <Box sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
        <Box component="span" sx={{ color: 'text.secondary', fontSize: 12, width: 34, flexShrink: 0, pt: '1px' }}>
            {label}
        </Box>
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={value}>
            {value || '—'}
        </Box>
    </Box>
);

const InvoiceDashboard = () => {
    const dispatch = useDispatch();
    const invoiceList = useSelector((state) => state.invoiceList.inprogressInv);
    const authInfo = useSelector((state) => state.authInfo);
    const refNotify = useSelector((state) => state.refNotify);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelReasonError, setCancelReasonError] = useState('');
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [cancelling, setCancelling] = useState(false);
    const [showAllRows, setShowAllRows] = useState(false);

    useEffect(() => {
        if (!(authInfo && authInfo.userId)) return undefined;
        dispatch(invoiceDateActions.getInvoicesData(authInfo.userId));
        const refreshId = setInterval(
            () => dispatch(invoiceDateActions.getInvoicesData(authInfo.userId, true)),
            60000
        );
        return () => clearInterval(refreshId);
    }, [dispatch, authInfo]);

    const closeCancel = () => {
        setSelectedInvoice(null);
        setCancelReason('');
        setCancelReasonError('');
    };

    const handleConfirmCancel = () => {
        if (!cancelReason.trim()) {
            setCancelReasonError('Enter a reason for cancelling.');
            return;
        }
        setCancelling(true);
        dispatch(invoiceDateActions.cancelInvoice(selectedInvoice, cancelReason))
            .then(() => {
                notify.createNotification('success', `Call ${selectedInvoice} cancelled.`, refNotify);
                dispatch(invoiceDateActions.getInvoicesData(authInfo.userId));
            })
            .catch((error) => {
                notify.createNotification('error', `Call ${selectedInvoice} couldn't be cancelled. Try again.`, refNotify);
                console.error('Error cancelling invoice:', error);
            })
            .finally(() => {
                setCancelling(false);
                closeCancel();
            });
    };

    const columns = useMemo(
        () => [
            {
                Header: 'Invoice',
                accessor: 'invoiceId',
                minWidth: 160,
                filterLabel: 'Invoice #',
                Filter: ColumnFilter,
                Cell: ({ row }) => (
                    <Box sx={{ borderLeft: `4px solid ${priorityColor(row.original.priority)}`, pl: 1.25 }}>
                        <Box sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{row.values.invoiceId}</Box>
                        <Chip
                            label={row.original.callStatus || 'Unknown'}
                            size="small"
                            variant="outlined"
                            sx={{ mt: 0.5, height: 22, fontSize: 12 }}
                        />
                    </Box>
                ),
            },
            { Header: 'Requested by', accessor: 'name', minWidth: 180, filterLabel: 'Name', Filter: ColumnFilter },
            {
                Header: 'Reason / rate plan',
                accessor: 'reason',
                minWidth: 170,
                filterLabel: 'Reason',
                Filter: ColumnFilter,
                Cell: ({ row }) => <TwoLine top={row.original.reason} bottom={row.original.accountPlanRateName} />,
            },
            {
                Header: 'Pickup and drop',
                accessor: 'fromLocation',
                minWidth: 260,
                filterLabel: 'Location',
                Filter: ColumnFilter,
                Cell: ({ row }) => (
                    <Box sx={{ maxWidth: 360, display: 'grid', gap: 0.25 }}>
                        <Place label="From" value={row.original.pickUpAddress} />
                        <Place label="To" value={row.original.dropOffAddress} />
                    </Box>
                ),
            },
            {
                Header: 'Driver / truck',
                accessor: 'driver',
                minWidth: 150,
                filterLabel: 'Driver',
                Filter: ColumnFilter,
                Cell: ({ row }) => (
                    <TwoLine top={row.original.driver} bottom={row.original.truck && `Truck ${row.original.truck}`} />
                ),
            },
            {
                Header: 'Elapsed',
                accessor: 'invoiceCreatedDate',
                minWidth: 100,
                disableFilters: true,
                Cell: ({ value }) => <ElapsedTimeCell startTime={value} />,
            },
            {
                Header: '',
                id: 'actions',
                disableFilters: true,
                Cell: ({ row }) => (
                    <Button
                        color="error"
                        size="small"
                        variant="outlined"
                        sx={{ whiteSpace: 'nowrap' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedInvoice(row.original.invoiceId);
                        }}
                    >
                        Cancel call
                    </Button>
                ),
            },
        ],
        []
    );

    const {
        getTableProps, getTableBodyProps, headerGroups, prepareRow, page, rows, gotoPage, setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        { columns, data: invoiceList || [], initialState: { pageSize: 25 }, autoResetFilters: false, autoResetPage: false },
        useFilters,
        usePagination
    );

    useEffect(() => {
        if (showAllRows) setPageSize(Math.max(rows.length, 1));
    }, [showAllRows, rows.length, setPageSize]);

    const statusCounts = useMemo(() => {
        const counts = {};
        (invoiceList || []).forEach((inv) => {
            const status = inv.callStatus || 'Unknown';
            counts[status] = (counts[status] || 0) + 1;
        });
        return Object.entries(counts).sort((a, b) => b[1] - a[1]);
    }, [invoiceList]);

    const total = (invoiceList || []).length;

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                <Typography variant="h6" component="h2">
                    {total} active {total === 1 ? 'call' : 'calls'}
                </Typography>
                {statusCounts.map(([status, count]) => (
                    <Chip key={status} label={`${status} ${count}`} size="small" sx={{ bgcolor: '#E7ECF3' }} />
                ))}
                <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                    Updates every minute. Click a row to open the invoice.
                </Typography>
            </Box>

            <Paper variant="outlined">
                <TableContainer>
                    <Table {...getTableProps()} size="small">
                        <TableHead>
                            {headerGroups.map((headerGroup) => (
                                <TableRow {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map((column) => (
                                        <TableCell
                                            {...column.getHeaderProps()}
                                            sx={{ verticalAlign: 'top', py: 1.25, minWidth: column.minWidth }}
                                        >
                                            <Box sx={{ whiteSpace: 'nowrap', minHeight: 20 }}>{column.render('Header')}</Box>
                                            {column.canFilter && <Box sx={{ mt: 1 }}>{column.render('Filter')}</Box>}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHead>
                        <TableBody {...getTableBodyProps()}>
                            {page.map((row) => {
                                prepareRow(row);
                                return (
                                    <TableRow
                                        hover
                                        {...row.getRowProps({
                                            onClick: () => window.open(`/invoice/${row.original.invoiceId}`, '_blank'),
                                        })}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        {row.cells.map((cell) => (
                                            <TableCell {...cell.getCellProps()} sx={{ py: 1.25, verticalAlign: 'top' }}>
                                                {cell.render('Cell')}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })}
                            {page.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={columns.length} sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                                        {total === 0 ? 'No active calls right now.' : 'No calls match these filters.'}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <AppPagination
                    count={rows.length}
                    page={pageIndex}
                    rowsPerPage={showAllRows ? ALL_ROWS : pageSize}
                    onPageChange={gotoPage}
                    onRowsPerPageChange={(value) => {
                        setShowAllRows(value === ALL_ROWS);
                        setPageSize(value === ALL_ROWS ? Math.max(rows.length, 1) : value);
                        gotoPage(0);
                    }}
                />
            </Paper>

            <Dialog open={Boolean(selectedInvoice)} onClose={cancelling ? undefined : closeCancel} fullWidth maxWidth="xs">
                <DialogTitle>Cancel call {selectedInvoice}?</DialogTitle>
                <DialogContent>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        Dispatch will be told the call is cancelled.
                    </Typography>
                    <TextField
                        label="Reason for cancelling"
                        fullWidth
                        autoFocus
                        multiline
                        minRows={2}
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        error={Boolean(cancelReasonError)}
                        helperText={cancelReasonError}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeCancel} disabled={cancelling}>
                        Keep call
                    </Button>
                    <Button variant="contained" color="error" onClick={handleConfirmCancel} disabled={cancelling}>
                        {cancelling ? 'Cancelling…' : 'Cancel call'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default withRouter(InvoiceDashboard);
